// ── Supabase Storage — photo upload ──────────────────────────────────────
// Replaces utils/firebaseStorage.js

import { supabase, storage } from './supabase';

// ── Upload profile photo ──────────────────────────────────────────────────
export async function uploadProfilePhoto(uid, file, index = 0, onProgress) {
  if (!file) return { url: null, error: 'No file' };
  if (!supabase) return { url: null, error: 'Supabase not configured' };

  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${uid}/photo_${index}_${Date.now()}.${ext}`;

  const { error } = await storage.profiles().upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });

  if (error) return { url: null, error: error.message };

  const { data } = storage.profiles().getPublicUrl(path);
  onProgress?.(100);
  return { url: data.publicUrl, error: null };
}

// ── Delete photo ──────────────────────────────────────────────────────────
export async function deleteProfilePhoto(url) {
  // Extract path from public URL
  const parts = url.split('/profiles/');
  if (parts.length < 2) return { error: 'Invalid URL' };
  const path = parts[1];
  const { error } = await storage.profiles().remove([path]);
  return { error: error?.message || null };
}

// ── Compress before upload (canvas) ──────────────────────────────────────
export function compressImage(file, maxW = 1200, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxW / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
        'image/jpeg', quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
}

// ── Full upload flow ──────────────────────────────────────────────────────
export async function addProfilePhoto(uid, file, currentPhotos = [], onProgress) {
  if (currentPhotos.length >= 6) return { photos: currentPhotos, error: 'Maximum 6 photos' };
  const compressed = await compressImage(file);
  const { url, error } = await uploadProfilePhoto(uid, compressed, currentPhotos.length, onProgress);
  if (error) return { photos: currentPhotos, error };
  const newPhotos = [...currentPhotos, url];
  // Update user_profiles directly (photos live there)
  const { dbUpdate } = await import('./supabase');
  await dbUpdate.userProfile(uid, { photos: newPhotos });
  return { photos: newPhotos, error: null };
}

// ── Reorder photos ────────────────────────────────────────────────────────
export async function reorderPhotos(uid, newOrder) {
  const { db } = await import('./supabase');
  const { dbUpdate: dbU } = await import('./supabase');
  await dbU.userProfile(uid, { photos: newOrder });
  return { photos: newOrder, error: null };
}

// ── Remove a photo ────────────────────────────────────────────────────────
export async function removeProfilePhoto(uid, photoUrl, currentPhotos = []) {
  await deleteProfilePhoto(photoUrl).catch(() => {});
  const newPhotos = currentPhotos.filter(p => p !== photoUrl);
  const { dbUpdate: dbUp } = await import('./supabase');
  await dbUp.userProfile(uid, { photos: newPhotos });
  return { photos: newPhotos, error: null };
}
